// Puerto de packages/hce/src/hce/paciente/FacesheetTab.tsx — layout de 3
// columnas densas (handoff "Clinical Instrument", #3a): Problemas y
// Medicamentos (280px) — Signos Vitales y Tendencias (flexible) — Cronología
// (300px), rehecho en Tailwind/shadcn. La "Nota rápida" con dictado queda
// debajo de las 3 columnas.
import { useMedplum, useSearchResources } from '@medplum/react-hooks';
import type { Condition, MedicationRequest, Observation } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams } from 'react-router';
import { BotonDictado } from '@/components/BotonDictado';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/sonner';
import { Textarea } from '@/components/ui/textarea';
import { formatearFechaRelativa } from './fechaRelativa';
import { traerHistoriaClinica, type HistoriaClinica } from './historiaClinica';
import { TrendSvgChart } from './TrendSvgChart';
import { agruparPorSignoVital, elegirSignoVitalConMasHistorial, imcCalculado, ultimaPresionArterial, valorObservacion } from './vitalesResumen';

function LabelSeccion({ children }: { children: JSX.Element | string }): JSX.Element {
  return <p className="mb-2 font-mono text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{children}</p>;
}

function FilaProblema({ condicion }: { condicion: Condition }): JSX.Element {
  const anio = condicion.onsetDateTime ?? condicion.recordedDate;
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border py-1.5">
      <span className="min-w-0 flex-1 truncate text-sm">{condicion.code?.text ?? condicion.code?.coding?.[0]?.display ?? '—'}</span>
      {anio && <span className="shrink-0 font-mono text-xs text-muted-foreground">{new Date(anio).getFullYear()}</span>}
    </div>
  );
}

function FilaMedicamento({ receta }: { receta: MedicationRequest }): JSX.Element {
  return (
    <div className="border-b border-border py-1.5">
      <p className="text-sm">{receta.medicationCodeableConcept?.text ?? '—'}</p>
      {receta.dosageInstruction?.[0]?.text && <p className="mt-0.5 font-mono text-xs text-muted-foreground">{receta.dosageInstruction[0].text}</p>}
    </div>
  );
}

function TileVital({ etiqueta, valor }: { etiqueta: string; valor: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-secondary/50 p-2">
      <p className="font-mono text-[9px] font-semibold text-muted-foreground">{etiqueta}</p>
      <p className="mt-0.5 text-lg font-medium">{valor}</p>
    </div>
  );
}

export function FacesheetTab(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const outlet = useOutletContext<{ patientId: string } | undefined>();
  const patientId = outlet?.patientId ?? id;
  const patientRef = `Patient/${patientId}`;

  const [condiciones] = useSearchResources('Condition', { patient: patientRef, _sort: '-recorded-date', _count: 100 });
  const [medicamentos] = useSearchResources('MedicationRequest', { patient: patientRef, status: 'active', _count: 100 });
  const [signos] = useSearchResources('Observation', { patient: patientRef, category: 'vital-signs', _sort: '-date', _count: 100 });

  const condicionesLista = (condiciones ?? []) as Condition[];
  const medicamentosLista = (medicamentos ?? []) as MedicationRequest[];
  const signosLista = (signos ?? []) as Observation[];

  const [historia, setHistoria] = useState<HistoriaClinica>();
  useEffect(() => {
    let cancelado = false;
    traerHistoriaClinica(medplum, patientRef).then((h) => {
      if (!cancelado) setHistoria(h);
    });
    return () => {
      cancelado = true;
    };
  }, [medplum, patientRef]);

  const [nota, setNota] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);

  const guardarNota = async (): Promise<void> => {
    if (!nota.trim()) return;
    setGuardandoNota(true);
    try {
      await medplum.createResource<Observation>({
        resourceType: 'Observation',
        status: 'final',
        subject: { reference: patientRef },
        code: { text: t('fichaPaciente.notaRapida') },
        valueString: nota.trim(),
        effectiveDateTime: new Date().toISOString(),
      });
      setNota('');
      notify({ color: 'green', message: t('questionnaireEngine.guardadoOk') });
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
    }
    setGuardandoNota(false);
  };

  const pa = ultimaPresionArterial(signosLista);
  const series = agruparPorSignoVital(signosLista);
  const valorDe = (clave: string): number | undefined => valorObservacion(series.find((s) => s.definicion.clave === clave)?.observaciones[0]);
  const fc = valorDe('fc');
  const spo2 = valorDe('sato2');
  const temp = valorDe('temperatura');
  const peso = valorDe('peso');
  const imc = imcCalculado(signosLista);

  const tendencia = elegirSignoVitalConMasHistorial(signosLista);
  const valoresTendencia = tendencia
    ? [...tendencia.observaciones]
        .reverse()
        .map((o) => valorObservacion(o))
        .filter((v): v is number => v !== undefined)
    : [];

  const eventos = historia
    ? [
        ...historia.encounters.map((e) => ({
          fecha: e.period?.start,
          titulo: t('fichaPaciente.historia.atencion'),
          descripcion: `${e.class?.display ?? e.class?.code ?? '—'} (${e.status ? t(`fichaPaciente.estadoAtencion.${e.status}`, e.status) : '—'})`,
        })),
        ...historia.conditions.map((c) => ({ fecha: c.recordedDate, titulo: c.code?.text ?? c.code?.coding?.[0]?.display ?? t('fichaPaciente.historia.diagnostico'), descripcion: t('fichaPaciente.historia.diagnostico') })),
        ...historia.medicationRequests.map((m) => ({ fecha: m.authoredOn, titulo: t('fichaPaciente.historia.receta'), descripcion: m.medicationCodeableConcept?.text ?? '—' })),
        ...historia.questionnaireResponses.map((qr) => ({
          fecha: qr.authored,
          titulo: t('fichaPaciente.historia.formulario'),
          descripcion: qr.status ? t(`fichaPaciente.estadoFormulario.${qr.status}`, qr.status) : '—',
        })),
      ]
        .filter((e) => e.fecha)
        .sort((a, b) => new Date(b.fecha as string).getTime() - new Date(a.fecha as string).getTime())
        .slice(0, 8)
    : [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr_300px]">
      <div className="md:border-r md:border-border md:pr-4">
        <LabelSeccion>{t('fichaPaciente.resumen.problemasActivos')}</LabelSeccion>
        <div className="mb-6">
          {condicionesLista.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('fichaPaciente.sinDatos')}</p>
          ) : (
            condicionesLista.map((c) => <FilaProblema key={c.id} condicion={c} />)
          )}
        </div>

        <LabelSeccion>{t('fichaPaciente.resumen.medicamentosActivos')}</LabelSeccion>
        <div>
          {medicamentosLista.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('fichaPaciente.sinDatos')}</p>
          ) : (
            medicamentosLista.map((m) => <FilaMedicamento key={m.id} receta={m} />)
          )}
        </div>
      </div>

      <div className="py-4 md:px-4 md:py-0">
        <LabelSeccion>{t('fichaPaciente.resumen.signosVitales')}</LabelSeccion>
        <div className="mb-4 grid grid-cols-3 gap-2">
          <TileVital etiqueta={t('fichaPaciente.vitalCorto.pa')} valor={pa?.sistolica !== undefined ? `${pa.sistolica}/${pa.diastolica ?? '—'}` : '—'} />
          <TileVital etiqueta={t('fichaPaciente.vitalCorto.fc')} valor={fc !== undefined ? `${fc}` : '—'} />
          <TileVital etiqueta={t('fichaPaciente.vitalCorto.spo2')} valor={spo2 !== undefined ? `${spo2}%` : '—'} />
          <TileVital etiqueta={t('fichaPaciente.vitalCorto.temp')} valor={temp !== undefined ? `${temp}°C` : '—'} />
          <TileVital etiqueta={t('fichaPaciente.vitalCorto.peso')} valor={peso !== undefined ? `${peso} kg` : '—'} />
          <TileVital etiqueta={t('fichaPaciente.vitalCorto.imc')} valor={imc !== undefined ? imc.toFixed(1) : '—'} />
        </div>

        {tendencia && valoresTendencia.length >= 2 && (
          <div className="mb-4 rounded-md border border-border p-3">
            <div className="mb-1.5 flex items-baseline justify-between">
              <p className="font-mono text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                {t('fichaPaciente.resumen.tendencia')} · {tendencia.definicion.etiqueta}
              </p>
              <p className="text-lg font-medium text-hce-clay">
                {valoresTendencia[valoresTendencia.length - 1]} {tendencia.definicion.unidad}
              </p>
            </div>
            <TrendSvgChart values={valoresTendencia} />
          </div>
        )}

        <div className="rounded-md border border-border p-4">
          <h3 className="mb-2 text-sm font-semibold">{t('fichaPaciente.notaRapida')}</h3>
          <div className="relative">
            <Textarea rows={2} placeholder={t('fichaPaciente.notaRapidaPlaceholder')} value={nota} onChange={(e) => setNota(e.currentTarget.value)} className="pr-10" />
            <div className="absolute top-1 right-1">
              <BotonDictado onTranscrito={(texto) => setNota((n) => (n ? `${n} ${texto}` : texto))} />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={() => void guardarNota()} disabled={guardandoNota || !nota.trim()}>
              {t('fichaPaciente.guardarNota')}
            </Button>
          </div>
        </div>
      </div>

      <div className="md:border-l md:border-border md:pl-4">
        <LabelSeccion>{t('fichaPaciente.resumen.cronologia')}</LabelSeccion>
        {eventos.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('fichaPaciente.resumen.sinAtencionesRegistradas')}</p>
        ) : (
          <div>
            {eventos.map((e, i) => (
              <div key={i} className="flex items-start gap-2 border-b border-border py-2">
                <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-hce-clay" />
                <div className="min-w-0">
                  <p className="font-mono text-[10px] text-muted-foreground">{formatearFechaRelativa(e.fecha)}</p>
                  <p className="mt-0.5 text-sm">{e.titulo}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{e.descripcion}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <Link to={`/${patientId}/historia`} className="mt-3 inline-block text-xs text-hce-pine hover:underline">
          {t('fichaPaciente.resumen.verHistoriaCompleta')}
        </Link>
      </div>
    </div>
  );
}
